import React from "react";
import { Fieldset } from "../Fieldset";
import { CheckboxInput } from "./CheckboxInput";
import { Error } from "../Error";
import { sanitiseString, concatStringFromClassesArrayList } from "../../Utils";
import type { ClassItem } from "../../Types/ClassItem";

export interface Props {
  checkboxesLabel?: string;
  checkboxesLabelClasses?: string;
  legend?: string;
  legendClasses?: string;
  legendIsQuestionTitle?: boolean;
  description?: string;
  checkboxes: CheckboxOptions;
  id?: string;
  name?: string;
  borderless?: boolean;
  classes?: string;
  dataTestId: string;
  dontWrap?: boolean;
  error?: React.ComponentProps<typeof Error>;
}

export interface CheckboxItem {
  id: string;
  name?: string;
  label: {
    text: string;
    description?: string;
  };
  value: string;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  checked?: boolean;
  disabled?: boolean;
}

export interface CheckboxOptions {
  dataTestId?: string;
  checkboxesList: CheckboxItem[];
}

export const Checkboxes = (props: Props) => {
  const sanitisedId: string = sanitiseString(props.id);
  const sanitisedDataTestId: string = sanitiseString(props.dataTestId);

  const fieldClassList: ClassItem = {
    defaultClass: "ons-fieldset",
    optionalClasses: [props.classes ? props.classes : null],
  };
  const fieldClassName: string =
    concatStringFromClassesArrayList(fieldClassList);

  const checkboxesLabelClassList: ClassItem = {
    defaultClass: "ons-checkboxes__label",
    optionalClasses: [
      props.checkboxesLabelClasses ? props.checkboxesLabelClasses : null,
    ],
  };
  const checkboxesLabelClassName: string = concatStringFromClassesArrayList(
    checkboxesLabelClassList,
  );

  const itemContainerClassList: ClassItem = {
    defaultClass: "ons-checkboxes__item",
    optionalClasses: [
      props.borderless ? "ons-checkboxes__item--no-border" : "",
    ],
  };
  const itemContainerClassName: string = concatStringFromClassesArrayList(
    itemContainerClassList,
  );

  const itemClassList: ClassItem = {
    optionalClasses: [props.borderless ? "ons-checkbox--no-border" : ""],
  };
  const itemClassName: string = concatStringFromClassesArrayList(itemClassList);

  const checkboxes = (
    <div className="ons-checkboxes__items">
      {props.checkboxes.checkboxesList.map((item, index) => {
        const sanitisedItemText: string = sanitiseString(item.label.text);
        const sanitisedItemId: string = sanitiseString(item.id);
        return (
          <React.Fragment key={index}>
            <span
              className={itemContainerClassName}
              data-testid={`${sanitisedDataTestId}-item-${sanitisedItemText}-container`}
              key={index}
            >
              <CheckboxInput
                id={sanitisedItemId}
                name={item.name}
                index={index}
                label={{
                  text: item.label.text,
                  description: item.label.description,
                }}
                value={item.value}
                classes={itemClassName}
                dataTestId={`${sanitisedDataTestId}-item-${sanitisedItemText}`}
                onClick={item.onClick}
                onChange={item.onChange}
                checked={item.checked}
                disabled={item.disabled}
              />
            </span>
            {index !== props.checkboxes.checkboxesList.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <Fieldset
      id={sanitisedId}
      classes={fieldClassName}
      legend={props.legend}
      legendClasses={props.legendClasses}
      description={props.description}
      dontWrap={props.dontWrap}
      legendIsQuestionTitle={props.legendIsQuestionTitle}
      error={props.error}
      dataTestId={`fieldset-${sanitisedDataTestId}`}
    >
      {props.checkboxesLabel && (
        <p className={checkboxesLabelClassName}>{props.checkboxesLabel}</p>
      )}
      {checkboxes}
    </Fieldset>
  );
};
